import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, CloudCog } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminLike } from "@/lib/roles";

/**
 * Phase 196 — Dashboard "Xero sync" health tile. One glance: is the accounting link healthy, retrying, stalled, dead,
 * or owned by another copy of BuiltOS? Click → Settings → Xero. Hidden entirely when Xero was never set up.
 */

const ago = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const m = Math.round(ms / 60000);
  if (m < 2) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 60) return `${d} days ago`;
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

const onDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "");

// tone → dot / badge classes (semantic colours already used across the dashboard)
const TONES = {
  good: { dot: "bg-emerald-400", badge: "border-emerald-500/30 bg-emerald-500/10 text-emerald-500 dark:text-emerald-300" },
  warn: { dot: "bg-amber-400", badge: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300" },
  bad: { dot: "bg-red-500", badge: "border-red-500/40 bg-red-500/10 text-red-500 dark:text-red-300" },
  info: { dot: "bg-primary", badge: "border-primary/30 bg-primary/10 text-primary" },
  muted: { dot: "bg-muted-foreground/50", badge: "border-border bg-muted/40 text-muted-foreground" },
};

export const describeXeroHealth = (h) => {
  if (!h) return null;
  const host = (h.owner_base_url || "").replace(/^https?:\/\//, "");
  switch (h.state) {
    case "reconnect":
      return {
        tone: "bad",
        label: "Reconnect needed",
        detail: `Stopped ${onDate(h.refresh_failed_at)} — bills & payments not syncing${h.self_heal_active ? " · retrying quietly" : ""}`,
      };
    case "elsewhere":
      return { tone: "info", label: "Runs elsewhere", detail: `Synced from ${host || "another copy of BuiltOS"}` };
    case "retrying":
      return { tone: "warn", label: "Retrying", detail: `Xero unreachable ${ago(h.refresh_last_error_at)} — next try within the hour` };
    case "stalled":
      return {
        tone: "warn",
        label: "Stalled",
        detail: h.last_pull_at ? `No pull since ${ago(h.last_pull_at)} (expected hourly)` : `Connected ${ago(h.connected_at)}, nothing pulled yet`,
      };
    case "paused":
      return { tone: "muted", label: "Auto-sync off", detail: h.last_pull_at ? `Last pull ${ago(h.last_pull_at)} · turn on in Settings` : "Turn on hourly pulls in Settings" };
    case "pending":
      return { tone: "info", label: "First sync pending", detail: `Connected ${ago(h.connected_at)} — first pull runs within the hour` };
    case "not_connected":
      return { tone: "muted", label: "Not connected", detail: "Connect your Xero organisation in Settings" };
    case "healthy":
      return { tone: "good", label: "Healthy", detail: `Last pull ${ago(h.last_pull_at)}${h.tenant_name ? ` · ${h.tenant_name}` : ""}` };
    default:
      return null;
  }
};

export const XeroHealthTile = ({ className = "" }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [health, setHealth] = useState(undefined); // undefined = loading, null = hidden / failed
  const allowed = isAdminLike(user) || user?.role === "team_member";

  useEffect(() => {
    if (!allowed) return undefined;
    let alive = true;
    const load = () =>
      api
        .get("/xero/health")
        .then((r) => alive && setHealth(r.data))
        .catch(() => alive && setHealth(null));
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [allowed]);

  if (!allowed || health === null || (health && health.state === "hidden")) return null;
  if (health === undefined) return <Skeleton className={`h-[52px] w-56 rounded-xl ${className}`} data-testid="xero-health-tile-loading" />;

  const d = describeXeroHealth(health);
  if (!d) return null;
  const tone = TONES[d.tone] || TONES.muted;

  return (
    <button
      type="button"
      onClick={() => navigate("/settings?tab=xero")}
      className={`group flex max-w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 text-left shadow-sm transition-colors duration-150 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
      title={`${d.label} — ${d.detail}. Open Settings → Xero`}
      aria-label={`Xero sync: ${d.label}. ${d.detail}. Open Xero settings`}
      data-testid="xero-health-tile"
      data-state={health.state}
      data-ro-allow
    >
      <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-primary">
        <CloudCog className="h-[18px] w-[18px]" aria-hidden="true" />
        <span className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-card ${tone.dot}`} data-testid="xero-health-dot" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Xero sync</span>
          <Badge variant="outline" className={`h-5 px-1.5 text-[10px] font-semibold ${tone.badge}`} data-testid="xero-health-label">
            {d.label}
          </Badge>
        </span>
        <span className="block max-w-[260px] truncate text-xs text-muted-foreground" data-testid="xero-health-detail">
          {d.detail}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
    </button>
  );
};
